import { useState, useEffect } from 'react';
import { Camera, Trash2, Plus, Upload, X, Download, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSignedUrls } from '../../hooks/useSignedUrl';
import { fetchStoredBytes } from '../../utils/fileUrls';

export function TrailerPhotosView() {
  const toast = useToast();
  const { profile } = useAuth();
  const [trailers, setTrailers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedTrailerId, setSelectedTrailerId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [trailerSearchQuery, setTrailerSearchQuery] = useState('');
  const [showTrailerDropdown, setShowTrailerDropdown] = useState(false);
  const [currentTrailerForPhotos, setCurrentTrailerForPhotos] = useState<any>(null);
  const [imageLoading, setImageLoading] = useState(false);
  // Photos are in a private bucket: sign the open lightbox's photos in one request.
  const { urls: signedPhotoUrls, loading: signedPhotosLoading } = useSignedUrls(showPhotoModal ? selectedPhotos : []);
  
  // Check if user can edit (Safety and Safety Manager can edit, Dispatchers can only download)
  const canEdit = profile?.role === 'Safety' || profile?.role === 'Safety Manager' || profile?.role === 'Superadmin' || profile?.role === 'superadmin' || profile?.role === 'Admin' || profile?.role === 'admin';

  useEffect(() => {
    loadTrailersWithPhotos();
  }, []);

  // Preload ALL images when modal opens for instant navigation
  useEffect(() => {
    if (showPhotoModal && selectedPhotos.length > 0 && !signedPhotosLoading) {
      setImageLoading(true);
      
      // Preload all images in the array (signed URLs)
      const imagePromises = selectedPhotos.map((url) => {
        const signed = signedPhotoUrls.get(url.trim());
        return new Promise((resolve, reject) => {
          if (!signed) { reject(new Error('unsigned')); return; }
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = signed;
        });
      });

      // Wait for current image to load before hiding spinner
      const currentSigned = signedPhotoUrls.get((selectedPhotos[currentPhotoIndex] || '').trim());
      if (currentSigned) {
        const currentImg = new Image();
        currentImg.onload = () => setImageLoading(false);
        currentImg.onerror = () => setImageLoading(false);
        currentImg.src = currentSigned;
      } else {
        setImageLoading(false);
      }

      // Preload rest in background (don't wait)
      Promise.all(imagePromises).catch(() => {});
    }
  }, [showPhotoModal, selectedPhotos, signedPhotoUrls, signedPhotosLoading]);

  // Set loading state when changing images
  useEffect(() => {
    if (showPhotoModal) {
      setImageLoading(true);
    }
  }, [currentPhotoIndex, showPhotoModal]);

  async function loadTrailersWithPhotos() {
    try {
      setLoading(true);
      
      // Load all trailers except INACTIVE ones
      const { data: trailersData, error: trailersError } = await supabase
        .from('trailers')
        .select('id, unit_number, vin')
        .neq('status', 'INACTIVE')
        .order('unit_number');

      if (trailersError) throw trailersError;

      // Load all trailer photos from trailer_load_photos table
      const { data: photosData, error: photosError } = await supabase
        .from('trailer_load_photos')
        .select('*')
        .order('created_at', { ascending: false });

      if (photosError) throw photosError;

      // Group photos by trailer number
      const photosMap = new Map<string, any[]>();
      (photosData || []).forEach(photo => {
        const existing = photosMap.get(photo.trailer_number) || [];
        photosMap.set(photo.trailer_number, [...existing, photo]);
      });

      // Combine trailers with their photos
      const trailersWithPhotos = (trailersData || []).map(trailer => ({
        ...trailer,
        photos: photosMap.get(trailer.unit_number) || []
      }));

      setTrailers(trailersWithPhotos);
    } catch (error) {
      console.error('Error loading trailers:', error);
      toast.error('Failed to load trailer photos');
    } finally {
      setLoading(false);
    }
  }

  const handleDeleteSinglePhoto = async (photoUrl: string) => {
    if (!confirm('Are you sure you want to delete this photo?')) return;

    try {
      // Delete the photo record from trailer_load_photos
      const { error } = await supabase
        .from('trailer_load_photos')
        .delete()
        .eq('photo_url', photoUrl);

      if (error) throw error;

      // Delete from storage
      const urlPath = photoUrl.split('/trailer-photos/')[1];
      if (urlPath) {
        await supabase.storage
          .from('company-documents')
          .remove([`trailer-photos/${urlPath}`]);
      }

      // Update local state
      const updatedPhotos = selectedPhotos.filter(url => url !== photoUrl);
      setSelectedPhotos(updatedPhotos);
      
      if (updatedPhotos.length === 0) {
        setShowPhotoModal(false);
      } else if (currentPhotoIndex >= updatedPhotos.length) {
        setCurrentPhotoIndex(updatedPhotos.length - 1);
      }

      toast.success('Photo deleted successfully');
      loadTrailersWithPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  const handleDeleteAllTrailerPhotos = async (trailerPhotos: any[]) => {
    if (!trailerPhotos || trailerPhotos.length === 0) return;
    
    if (!confirm(`Are you sure you want to delete all photos for this trailer? This will remove ${trailerPhotos.length} photo(s).`)) return;

    try {
      // Delete all photo records for this trailer
      for (const photoRecord of trailerPhotos) {
        const { error } = await supabase
          .from('trailer_load_photos')
          .delete()
          .eq('id', photoRecord.id);

        if (error) throw error;
      }

      toast.success('All trailer photos deleted successfully');
      loadTrailersWithPhotos();
    } catch (error) {
      console.error('Error deleting trailer photos:', error);
      toast.error('Failed to delete trailer photos');
    }
  };

  const openPhotoModal = (photos: string[], trailer: any) => {
    setSelectedPhotos(photos);
    setCurrentPhotoIndex(0);
    setCurrentTrailerForPhotos(trailer);
    setShowPhotoModal(true);
  };

  const getAllPhotosForTrailer = (trailerPhotos: any[]) => {
    // Each photo record has one photo_url
    return trailerPhotos.map(photo => photo.photo_url).filter(Boolean);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const handleUploadPhotos = async () => {
    if (!selectedTrailerId) {
      toast.error('Please select a trailer');
      return;
    }
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }

    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const selectedTrailer = trailers.find(t => t.id === selectedTrailerId);
      if (!selectedTrailer) throw new Error('Trailer not found');

      const uploadedUrls: string[] = [];

      // Upload each file to company-documents storage
      for (const file of selectedFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${selectedTrailer.unit_number}_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `trailer-photos/${selectedTrailer.unit_number}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('company-documents')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      // Save each photo to trailer_load_photos table
      const photoInserts = uploadedUrls.map(url => ({
        trailer_number: selectedTrailer.unit_number,
        photo_url: url,
        uploaded_by: user?.email || 'unknown'
      }));

      const { error: dbError } = await supabase
        .from('trailer_load_photos')
        .insert(photoInserts);

      if (dbError) throw dbError;

      toast.success(`${selectedFiles.length} photo(s) uploaded successfully!`);
      setShowUploadModal(false);
      setSelectedTrailerId('');
      setSelectedFiles([]);
      loadTrailersWithPhotos();
    } catch (error: any) {
      console.error('Error uploading photos:', error);
      toast.error(`Failed to upload photos: ${error.message || 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadPhotos = async (trailerPhotos: any[]) => {
    const allPhotos = getAllPhotosForTrailer(trailerPhotos);
    if (allPhotos.length === 0) {
      toast.error('No photos to download');
      return;
    }

    let downloaded = 0;
    for (let i = 0; i < allPhotos.length; i++) {
      try {
        const blob = await fetchStoredBytes(allPhotos[i]);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `photo_${i + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        downloaded++;
      } catch {
        // Counted below; the error can carry the stored URL, so it is not logged.
      }

      // Add small delay between downloads to avoid browser blocking
      if (i < allPhotos.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    if (downloaded === allPhotos.length) {
      toast.success(`Downloaded ${downloaded} photo(s)`);
    } else if (downloaded > 0) {
      toast.error(`Downloaded ${downloaded} of ${allPhotos.length} photos; ${allPhotos.length - downloaded} failed`);
    } else {
      toast.error('Failed to download photos');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading trailers...</div>
      </div>
    );
  }

  // Filter trailers: show all trailers, then filter by search query
  const filteredTrailers = trailers.filter(trailer => {
    // Apply search filter
    const query = searchQuery.toLowerCase();
    if (!query) return true; // Show all if no search query
    
    return (
      trailer.unit_number?.toLowerCase().includes(query) ||
      trailer.vin?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Camera className="w-6 h-6" />
              Trailer Photos
            </h1>
            <p className="text-slate-600 mt-1">
              {canEdit ? 'View and manage trailer photos' : 'View and download trailer photos'} • {trailers.length} {trailers.length === 1 ? 'trailer' : 'trailers'}
            </p>
          </div>
          {canEdit && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Add Trailer
            </button>
          )}
        </div>
        
        {/* Search Bar */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by unit number or VIN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Unit Number</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Photos</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">VIN</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredTrailers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Search className="w-12 h-12 text-slate-300" />
                      <p className="text-slate-500 font-medium">
                        {searchQuery ? 'No trailers match your search' : 'No trailers found'}
                      </p>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTrailers.map((trailer) => {
                  const allPhotos = getAllPhotosForTrailer(trailer.photos);
                  return (
                    <tr key={trailer.id} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-bold text-slate-900">{trailer.unit_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        {allPhotos.length > 0 ? (
                          <button
                            onClick={() => openPhotoModal(allPhotos, trailer)}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors border border-blue-200 hover:border-blue-300"
                          >
                            <Camera className="w-4 h-4" />
                            <span className="text-sm font-semibold">{allPhotos.length} photo{allPhotos.length !== 1 ? 's' : ''}</span>
                          </button>
                        ) : (
                          <div className="flex items-center gap-2 text-slate-400">
                            <Camera className="w-4 h-4" />
                            <span className="text-sm">No photos</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600 font-mono">{trailer.vin || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {canEdit && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedTrailerId(trailer.id);
                                  setShowUploadModal(true);
                                }}
                                className="p-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all hover:shadow-sm"
                                title="Add more photos"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteAllTrailerPhotos(trailer.photos)}
                                className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all hover:shadow-sm"
                                title="Delete all photos"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDownloadPhotos(trailer.photos)}
                            className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all hover:shadow-sm"
                            title="Download all photos"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Photo Modal */}
      {showPhotoModal && selectedPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl w-full">
            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
              {canEdit && (
                <button
                  onClick={() => handleDeleteSinglePhoto(selectedPhotos[currentPhotoIndex])}
                  className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  title="Delete this photo"
                >
                  <Trash2 className="w-5 h-5" />
                  Delete
                </button>
              )}
              <button
                onClick={() => setShowPhotoModal(false)}
                className="p-2 bg-white text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="relative min-h-[400px] flex items-center justify-center">
              {(signedPhotosLoading || (imageLoading && signedPhotoUrls.get((selectedPhotos[currentPhotoIndex] || '').trim()))) && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                </div>
              )}
              {!signedPhotosLoading && !signedPhotoUrls.get((selectedPhotos[currentPhotoIndex] || '').trim()) && (
                <div className="text-white text-sm">This photo could not be loaded</div>
              )}
              {!signedPhotosLoading && signedPhotoUrls.get((selectedPhotos[currentPhotoIndex] || '').trim()) && (
                <img
                  key={currentPhotoIndex}
                  src={signedPhotoUrls.get((selectedPhotos[currentPhotoIndex] || '').trim())}
                  alt={`Photo ${currentPhotoIndex + 1}`}
                  className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
                  onLoad={() => setImageLoading(false)}
                  onError={() => setImageLoading(false)}
                  style={{ display: imageLoading ? 'none' : 'block' }}
                />
              )}
            </div>
            
            {selectedPhotos.length > 1 && (
              <div className="flex items-center justify-center gap-4 mt-4">
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : selectedPhotos.length - 1))}
                  className="px-4 py-2 bg-white text-slate-900 rounded-lg hover:bg-slate-100"
                >
                  Previous
                </button>
                <span className="text-white">
                  {currentPhotoIndex + 1} / {selectedPhotos.length}
                </span>
                <button
                  onClick={() => setCurrentPhotoIndex((prev) => (prev < selectedPhotos.length - 1 ? prev + 1 : 0))}
                  className="px-4 py-2 bg-white text-slate-900 rounded-lg hover:bg-slate-100"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-slate-900">Upload Trailer Photos</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedTrailerId('');
                  setSelectedFiles([]);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Trailer Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Trailer *
                </label>
                
                {/* Search Input with Autocomplete */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                  <input
                    type="text"
                    value={trailerSearchQuery}
                    onChange={(e) => {
                      setTrailerSearchQuery(e.target.value);
                      setShowTrailerDropdown(true);
                      if (!e.target.value) {
                        setSelectedTrailerId('');
                      }
                    }}
                    onFocus={() => setShowTrailerDropdown(true)}
                    placeholder="Search by unit number or VIN..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  {/* Dropdown Results */}
                  {showTrailerDropdown && trailerSearchQuery && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {trailers
                        .filter(trailer => {
                          const query = trailerSearchQuery.toLowerCase();
                          return trailer.unit_number?.toLowerCase().includes(query) ||
                            trailer.vin?.toLowerCase().includes(query);
                        })
                        .slice(0, 10)
                        .map((trailer) => (
                          <button
                            key={trailer.id}
                            type="button"
                            onClick={() => {
                              setSelectedTrailerId(trailer.id);
                              setTrailerSearchQuery(trailer.unit_number);
                              setShowTrailerDropdown(false);
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors border-b border-slate-100 last:border-b-0"
                          >
                            <div className="font-medium text-slate-900">{trailer.unit_number}</div>
                            {trailer.vin && (
                              <div className="text-xs text-slate-600 font-mono">{trailer.vin}</div>
                            )}
                          </button>
                        ))}
                      {trailers.filter(trailer => {
                        const query = trailerSearchQuery.toLowerCase();
                        return trailer.unit_number?.toLowerCase().includes(query) ||
                          trailer.vin?.toLowerCase().includes(query);
                      }).length === 0 && (
                        <div className="px-4 py-3 text-sm text-slate-500 text-center">
                          No trailers found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {selectedTrailerId && (
                  <p className="mt-2 text-sm text-slate-600">
                    VIN: {trailers.find(t => t.id === selectedTrailerId)?.vin || 'N/A'}
                  </p>
                )}
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Photos *
                </label>
                <label className="cursor-pointer">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 hover:border-blue-500 transition-colors">
                    <div className="flex flex-col items-center justify-center gap-2 text-slate-600">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm font-medium">Click to select photos</span>
                      <span className="text-xs text-slate-500">or drag and drop</span>
                    </div>
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-3">
                    {selectedFiles.length} file(s) selected
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-full h-24 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <p className="text-xs text-slate-600 mt-1 truncate">{file.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedTrailerId('');
                    setSelectedFiles([]);
                  }}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadPhotos}
                  disabled={uploading || !selectedTrailerId || selectedFiles.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload Photos'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
